import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Grid
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { TWEET_INTERVAL } from "../../../../constants";

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  emptyMessage: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '1.1rem',
    padding: theme.spacing(3),
  },
  jobItem: {
    marginBottom: theme.spacing(3),
    position: 'relative',
    '&.inactive': {
      backgroundColor: theme.palette.grey[500],
    },
  },
  jobContainer: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  card: {
    marginBottom: theme.spacing(2),
    boxShadow: 'none',
    borderRadius: '0.25rem',
    backgroundColor: 'transparent',
    position: 'relative',
    '& .MuiCard-root': {
      boxShadow: 'none',
    },
  },
  cardHeader: {
    padding: theme.spacing(2),
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    backgroundColor: 'transparent',
    '& .MuiCardHeader-title': {
      fontSize: '1.6rem',
      fontWeight: 600,
      color: '#363636',
      lineHeight: 1.125,
    },
  },
  viewCount: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    fontSize: '1rem',
    color: '#4a4a4a',
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(0.5),
      fontSize: '1.4rem',
    },
  },
  cardContent: {
    padding: theme.spacing(3),
    backgroundColor: 'transparent',
  },
  detailList: {
    margin: 0,
    '& dt': {
      fontWeight: 600,
      color: '#363636',
      fontSize: '1.4rem',
      marginBottom: theme.spacing(1),
    },
    '& dd': {
      marginLeft: 0,
      marginBottom: theme.spacing(2),
      fontSize: '1.2rem',
      color: '#4a4a4a',
    },
  },
  tag: {
    margin: theme.spacing(0.5),
    fontSize: '0.875rem',
    height: '2em',
    padding: '0 0.75em',
    borderRadius: '4px',
    fontWeight: 400,
  },
  primaryTag: {
    backgroundColor: '#00d1b2',
    color: '#fff',
  },
  successTag: {
    backgroundColor: '#48c774',
    color: '#fff',
  },
  lightTag: {
    backgroundColor: '#ebfffc',
    color: '#00947e',
  },
  buttons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '& .MuiButton-root': {
      textTransform: 'none',
      fontSize: '1.2rem',
      fontWeight: 400,
      padding: '0.5em 1em',
      borderRadius: '4px',
      minWidth: '100px',
      '&.MuiButton-containedPrimary': {
        backgroundColor: '#3273dc',
        '&:hover': {
          backgroundColor: '#2366d1',
        },
      },
      '&.MuiButton-containedSecondary': {
        backgroundColor: '#f14668',
        '&:hover': {
          backgroundColor: '#ef2e55',
        },
      },
      '&.MuiButton-containedWarning': {
        backgroundColor: '#ffdd57',
        color: 'rgba(0, 0, 0, 0.7)',
        '&:hover': {
          backgroundColor: '#ffd83d',
        },
      },
    },
  },
  shareButton: {
    display: 'flex',
    justifyContent: 'center',
    '& .MuiButton-root': {
      fontSize: '1rem',
      backgroundColor: '#48c774',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#3ec46d',
      },
      '&.Mui-disabled': {
        backgroundColor: '#dbdbdb',
        color: '#7a7a7a',
      },
      '& .MuiSvgIcon-root': {
        fontSize: '1.25rem',
        marginRight: theme.spacing(0.5),
      },
    },
  },
  disabledText: {
    color: '#7a7a7a',
  },
  gridContainer: {
    marginTop: theme.spacing(2),
    '& .MuiGrid-item': {
      padding: theme.spacing(1.5),
    },
  },
  warningButton: {
    backgroundColor: '#ffdd57 !important',
    color: 'rgba(0, 0, 0, 0.7) !important',
    '&:hover': {
      backgroundColor: '#ffd83d !important',
    },
  },
  inactiveOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    fontSize: '1.4rem',
    fontWeight: 600,
    zIndex: 10,
    pointerEvents: 'none',
  },
}));

const PostedJobList = props => {
  const classes = useStyles();
  const { jobList, companyVerified = false } = props;

  const handleDeleteJob = id => {
    props.onDelete(id);
  };

  const handleActivateJob = id => {
    props.onActivate(id);
  };

  const handleDeactivateJob = id => {
    props.onDeactivate(id);
  };

  const handleTweet = id => {
    props.onTweet(id);
  };

  const renderTags = tags => {
    if (tags && tags.length > 0) {
      return tags.map(tag => (
        <Chip
          key={tag}
          label={tag}
          className={`${classes.tag} ${classes.lightTag}`}
          size="small"
        />
      ));
    }
    return "未設定";
  };

  const renderJobTable = job => {
    return (
      <dl className={classes.detailList}>
        <dt>エリア</dt>
        <dd>
          {job.area ? (
            <Chip
              label={job.area.name}
              className={`${classes.tag} ${classes.primaryTag}`}
              size="small"
            />
          ) : (
            "未設定"
          )}
        </dd>
        <dt>職種</dt>
        <dd>
          <Chip
            label={job.jobCategory}
            className={`${classes.tag} ${classes.successTag}`}
            size="small"
          />
        </dd>
        <dt>詳細</dt>
        <dd>{job.details}</dd>
        <dt>勤務タイプ</dt>
        <dd>{job.workType ? job.workType : "不問"}</dd>
        <dt>必要英語レベル</dt>
        <dd>{job.englishLevel ? job.englishLevel : "不問"}</dd>
        <dt>タグ</dt>
        <dd>{renderTags(job.tags)}</dd>
      </dl>
    );
  };

  const enableTweet = job => {
    if (!job.is_active) return false;
    if (!job.tweetedAt) return true;

    let timeDiff = Date.now() - new Date(job.tweetedAt).getTime();
    return timeDiff > TWEET_INTERVAL ? true : false;
  };

  const shareButtonStatus = job => {
    let timeDiff = Date.now() - new Date(job.tweetedAt).getTime();
    if (timeDiff > TWEET_INTERVAL) return null;

    let waitDate = (TWEET_INTERVAL - timeDiff) / 86400000;
    return `あと${Math.round(waitDate) + 1}日で再告知可能`;
  };

  if (!jobList || jobList.length === 0) {
    return (
      <Typography className={classes.emptyMessage}>
        データがありません
      </Typography>
    );
  }

  return (
    <Box className={classes.root}>
      <Grid container spacing={3} className={classes.gridContainer}>
        {jobList.map(job => (
          <Grid item xs={12} md={6} key={job._id} >
            <Box className={classes.jobContainer}>
              <Box className={`${classes.jobItem} ${!job.is_active ? 'inactive' : ''}`}>
                <Card className={classes.card}>
                  <CardHeader
                    title={job.title}
                    className={classes.cardHeader}
                  />
                  <Box className={classes.viewCount}>
                    <VisibilityIcon />
                    <span>x {job.viewCount}</span>
                  </Box>
                  <CardContent className={classes.cardContent}>
                    {renderJobTable(job)}
                  </CardContent>
                </Card>
                {job.is_active ? null : (
                  <div className={classes.inactiveOverlay}>
                    募集停止中
                  </div>
                )}
              </Box>
              <Box className={classes.buttons}>
                <Button
                  component={Link}
                  to={`/jobs/edit/${job._id}`}
                  variant="contained"
                  color="primary"
                >
                  編集
                </Button>
                {job.is_active ? (
                  <Button
                    variant="contained"
                    className={classes.warningButton}
                    onClick={() => handleDeactivateJob(job._id)}
                  >
                    募集停止
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleActivateJob(job._id)}
                    style={{ backgroundColor: '#48c774' }}
                  >
                    募集再開
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleDeleteJob(job._id)}
                >
                  削除
                </Button>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

PostedJobList.propTypes = {
  onActivate: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onTweet: PropTypes.func,
  companyVerified: PropTypes.bool
};

export default PostedJobList;
